import { useState, useRef, useCallback, useEffect } from 'react';
import Lightbox from './Lightbox';

function ManagerSection({ title, titleColor, subtitle, subtitleColor, managers }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const scrollRef = useRef(null);
  const isJumping = useRef(false);

  const lightboxImages = managers.map(m => ({ src: m.image, alt: m.name }));
  const tripled = [...managers, ...managers, ...managers];

  const jumpToMiddle = useCallback(() => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const singleWidth = container.scrollWidth / 3;
    container.scrollLeft = singleWidth;
  }, []);

  useEffect(() => {
    jumpToMiddle();
  }, [jumpToMiddle]);

  const handleScroll = useCallback(() => {
    if (isJumping.current || !scrollRef.current) return;
    const container = scrollRef.current;
    const singleWidth = container.scrollWidth / 3;

    if (container.scrollLeft <= 0) {
      isJumping.current = true;
      container.style.scrollBehavior = 'auto';
      container.scrollLeft = singleWidth;
      container.style.scrollBehavior = '';
      isJumping.current = false;
    } else if (container.scrollLeft >= singleWidth * 2) {
      isJumping.current = true;
      container.style.scrollBehavior = 'auto';
      container.scrollLeft = singleWidth;
      container.style.scrollBehavior = '';
      isJumping.current = false;
    }
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({
      left: direction === 'next' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  };

  const getRealIndex = (tripledIndex) => tripledIndex % managers.length;

  return (
    <section className="py-10 sm:py-[60px] bg-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className={`font-montserrat text-xs tracking-[0.3em] uppercase mb-4 ${subtitleColor ? '' : 'text-gold'}`} style={subtitleColor ? { color: subtitleColor } : undefined}>{subtitle}</p>
          <h2 className={`font-playfair text-4xl sm:text-5xl font-bold ${titleColor ? '' : 'gold-gradient-text'}`} style={titleColor ? { color: titleColor } : undefined}>{title}</h2>
        </div>
      </div>

      <div className="relative">
        <button
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-white/80 hover:text-gold bg-black/50 hover:bg-black/70 rounded-full transition-all duration-300"
          onClick={() => scroll('prev')}
        >
          <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-white/80 hover:text-gold bg-black/50 hover:bg-black/70 rounded-full transition-all duration-300"
          onClick={() => scroll('next')}
        >
          <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div
          ref={scrollRef}
          className="flex gap-4 sm:gap-6 overflow-x-auto px-4 sm:px-12 scrollbar-hide"
          style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
        >
          {tripled.map((m, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-[60vw] sm:w-[40vw] md:w-[28vw] lg:w-[20vw] gold-border gold-border-hover transition-all duration-300 group overflow-hidden cursor-pointer"
              style={{ scrollSnapAlign: 'start' }}
              onClick={() => setSelectedIndex(getRealIndex(i))}
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src={m.image}
                  alt={m.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedIndex !== null && (
        <Lightbox
          images={lightboxImages}
          currentIndex={selectedIndex}
          onClose={() => setSelectedIndex(null)}
        />
      )}
    </section>
  );
}

export default ManagerSection;
